import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

import { auth, db } from '@/lib/firebase';
import { slugify } from '@/lib/utils';
import { TRIAL_PLAN, trialEndFrom } from '@/lib/access';
import type { Owner } from '@/types/owner';

export { slugify };

export type RegisterInput = {
  fullName: string;
  email: string;
  password: string;
  shopName: string;
  whatsapp?: string;
  phone?: string;
};

export async function registerOwner(input: RegisterInput): Promise<Owner> {
  const cred = await createUserWithEmailAndPassword(auth, input.email.trim(), input.password);
  if (input.fullName) {
    await updateProfile(cred.user, { displayName: input.fullName });
  }

  const now = Date.now();
  const owner: Owner = {
    ownerId: cred.user.uid,
    fullName: input.fullName.trim(),
    email: input.email.trim().toLowerCase(),
    shopName: input.shopName.trim(),
    shopSlug: slugify(input.shopName) || cred.user.uid,
    // Essai gratuit : accès Business pendant TRIAL_DAYS jours dès l'inscription.
    plan: TRIAL_PLAN,
    subscriptionStatus: 'trialing',
    trialEndsAt: trialEndFrom(now),
    accountStatus: 'active',
    createdAt: now,
    updatedAt: now,
  };
  if (input.whatsapp) owner.whatsapp = input.whatsapp.trim();
  if (input.phone) owner.phone = input.phone.trim();

  await setDoc(doc(db, 'owners', cred.user.uid), {
    ...owner,
    createdAtServer: serverTimestamp(),
  });

  return owner;
}

export async function loginOwner(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email.trim(), password);
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email.trim());
}

export async function logoutOwner(): Promise<void> {
  await signOut(auth);
}

export async function getCurrentOwner(ownerId: string): Promise<Owner | null> {
  const snap = await getDoc(doc(db, 'owners', ownerId));
  return snap.exists() ? (snap.data() as Owner) : null;
}
